import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { GenericTableColumn } from 'src/app/shared/interfaces/genetic-table-column';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';
import { IOrderSaleReturn } from '../../interfaces/order-sale-return.interface';
import { FieldType } from 'src/app/shared/interfaces/field-type.enum';
import { RowType } from 'src/app/shared/interfaces/raw-type.enum';
import { IProvider } from 'src/app/shared/interfaces/provider.interface';
import { CreateProvider, GetProvidersList, GetSalesToProviders, SaveNewSaleOrReturn as SaveNewSaleOrReturn } from 'src/app/shared/states/orders/orders.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ISellReturnOrderDialogSubmittedEvent } from '../../interfaces/sell-return-order-dialog-submitted-event.interface';
import { PinService } from 'src/app/shared/services/pin.service';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { OrderTypeEnum } from 'src/app/shared/interfaces/OrderTypeEnum';
import { OrdersService } from '../receive-edi/orders.service';
import { filterTable } from 'src/app/shared/helpers/helpers';
import { Router } from '@angular/router';
import { FileService } from 'src/app/shared/services/file-service';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { IUserName } from 'src/app/shared/interfaces/user-name.interface';
import { PdfSignatureService } from 'src/app/shared/services/pdf-signature-service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';

@UntilDestroy()
@Component({
  selector: 'app-sales-to-providers',
  templateUrl: './sales-to-providers.component.html',
  styleUrls: ['./sales-to-providers.component.scss',  './../../inventory.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SalesToProvidersComponent implements OnInit {

  @Select(OrdersState.loading) loading$: Observable<boolean | undefined>;
  @Select(OrdersState.providers) providers$: Observable<IProvider[]>;
  @Select(OrdersState.salesReturns) salesReturns$: Observable<IOrderSaleReturn[]>;
  
  filteredSales$: BehaviorSubject<IOrderSaleReturn[]> = new BehaviorSubject([]);

  SearchInputColorEnum = SearchInputColorEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  OrderTypeEnum = OrderTypeEnum;

  sellToProviderDialog: boolean;


   columns: GenericTableColumn<IOrderSaleReturn>[] = [
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Recorded', field: 'createDate',  RowType: RowType.Double,
      doubleRowField: {
        headingField: 'createDate', headingFieldType: FieldType.DateTime, 
        secondaryField1: 'userFirstName', secondaryFieldType1: FieldType.Text, secondaryField2: 'userLastName', secondaryFieldType2: FieldType.LastName
      },
    }),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Ship Date', field: 'shipDate', fieldType: FieldType.Date}),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Buyer', field: 'soldReturnedTo'}),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Po #', field: 'purchaseOrder', sortable: false}),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Invoice #', field: 'invoiceNumber', sortable: false}),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'csos #', field: 'csos', sortable: false}),
    new GenericTableColumn<IOrderSaleReturn>({ header: 'Ndcs', field: 'ndcCount', fieldType: FieldType.Number, sortable: false}),
  ];

  private sellToProviderPinChannel = "sellToProvider";
  private eventPayload: ISellReturnOrderDialogSubmittedEvent;

  /**
   *
   */
  constructor(
    private store: Store, 
    private pinService: PinService, 
    private orderService: OrdersService,
    private router: Router,
    private fileService: FileService,
    private backCountService: BackCountService,
    private pdfSignatureService: PdfSignatureService, 
  ) {
    
  }

  ngOnInit(): void {
    this.store.dispatch(new GetSalesToProviders());

    this.salesReturns$.pipe(untilDestroyed(this))
    .subscribe(data => {
      this.filteredSales$.next(data);
    })
    
    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(async res => {
      if (res?.dialogId === this.sellToProviderPinChannel && res?.success) {
         await this.saveSubmittedSale(res.pin, res.response?.user);
        //TODO proceed with files
      }
    })
  }

   async saveSubmittedSale(pin: number, userName: IUserName): Promise<void> {
    if (this.eventPayload) {
      this.store.dispatch(new SaveNewSaleOrReturn({...this.eventPayload, pin, activityType: ActivityTypesEnum.SoldToProvider, orderType: OrderTypeEnum.SaleToProvider}))
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        const orderID = this.store.selectSnapshot(OrdersState.createdOrderID);
        if (orderID) {
          if (this.eventPayload.orderAttachments?.length) {
            const name = userName?.firstName + ' ' + userName?.lastName;
            const pharmaName = this.store.selectSnapshot(PharmacyState.currentPharmacy).name;
            await this.pdfSignatureService.sign2(this.eventPayload.orderAttachments,name, pharmaName, new Date());
          }
        
          this.fileService.saveAttachmentsList(
            {
              orderId: orderID,
              pin, 
              orderAttachments: this.eventPayload.orderAttachments?.map(x => ({id: x.id, name: x.name, type: x.type, otherType: x.otherType}))
            }, false, [], this.eventPayload.orderAttachments
          );
          this.store.dispatch(new GetSalesToProviders());
          this.pinService.closeDialog();
          this.backCountService.handleBackCountPillUpdate(this.eventPayload.sellReturnNdcs);
        }
      });
    }
  }

  searchValueChanged(val: string): void {
    const filtered = filterTable(this.store.selectSnapshot(OrdersState.salesReturns), val, 'soldReturnedTo', 'purchaseOrder', 'csos', 'invoiceNumber');
    this.filteredSales$.next(filtered);
  }

  openDetail(id: string): void {
    this.router.navigate(['inventory/sales-to-providers-history', id]);
  }

  openSaleToProviderDialog(): void {
    if (!this.store.selectSnapshot(OrdersState.providers)?.length) {
      this.store.dispatch(new GetProvidersList())
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.sellToProviderDialog = true;
      });
    }
    this.sellToProviderDialog = true;
  }
  
  saleToProviderSubmitted(event: ISellReturnOrderDialogSubmittedEvent): void {
    this.eventPayload = event;
    this.sellToProviderDialog = false;
    this.pinService.openPinDialog(this.sellToProviderPinChannel);
  }
}
